<template>
  <div>
    <b-alert
      variant="secondary"
      show
    >
      <div class="alert-body">
        {{ $t('Por padrão, todos os dias seguem a Configuração base. Ative "Personalizar" em um dia para definir horário, taxa por KM ou faixas diferentes apenas nele.') }}
      </div>
    </b-alert>

    <!-- Resumo do PADRÃO (configuração base) que os dias sem personalização herdam. -->
    <b-card
      class="bg-light-secondary mb-1"
      body-class="py-1"
    >
      <div class="d-flex align-items-center mb-50">
        <feather-icon
          icon="AnchorIcon"
          size="16"
          class="mr-50"
        />
        <strong>{{ $t('Padrão herdado pelos dias sem personalização') }}</strong>
      </div>

      <b-row>
        <b-col
          md="3"
          class="mb-50"
        >
          <small class="text-muted d-block">{{ $t('Delivery') }}</small>
          {{ defaultEnabledLabel }}
        </b-col>
        <b-col
          md="3"
          class="mb-50"
        >
          <small class="text-muted d-block">{{ $t('Taxa por KM') }}</small>
          {{ defaultPerKmLabel }}
        </b-col>
        <b-col
          md="3"
          class="mb-50"
        >
          <small class="text-muted d-block">{{ $t('Valor mínimo') }}</small>
          {{ defaultMinOrderLabel }}
        </b-col>
      </b-row>

      <div class="mt-50">
        <small class="text-muted d-block">{{ $t('Faixas padrão') }}</small>
        <span
          v-for="(tier, tIndex) in config.defaultTiers"
          :key="tIndex"
          class="mr-1"
        >
          {{ $t('A partir de') }} {{ formatCurrency(tier.productsValueFrom) }} —
          {{ tier.value === 0 ? $t('grátis') : formatCurrency(tier.value) }}<span v-if="tIndex < config.defaultTiers.length - 1">;</span>
        </span>
      </div>

      <small class="text-muted d-block mt-50">
        {{ $t('Os campos "Abre"/"Fecha" deixados em branco herdam o horário de funcionamento da loja.') }}
      </small>
    </b-card>

    <div
      v-for="day in config.days"
      :key="day.dayOfWeek"
      class="border rounded mb-50"
    >
      <!-- Cabeçalho do dia: nome, status e o botão de personalizar. -->
      <div class="d-flex align-items-center justify-content-between px-1 py-75">
        <div class="d-flex align-items-center">
          <strong
            class="mr-1"
            style="min-width: 130px; display: inline-block"
          >{{ dayLabel(day.dayOfWeek) }}</strong>
          <b-badge :variant="isShown(day.dayOfWeek) ? 'light-primary' : 'light-secondary'">
            {{ isShown(day.dayOfWeek) ? $t('Personalizado') : $t('Segue o padrão') }}
          </b-badge>
        </div>
        <b-form-checkbox
          :id="`day-${day.dayOfWeek}-personalize`"
          switch
          :checked="isShown(day.dayOfWeek)"
          @change="checked => togglePersonalize(day, checked)"
        >
          {{ $t('Personalizar') }}
        </b-form-checkbox>
      </div>

      <b-collapse :visible="isShown(day.dayOfWeek)">
        <div class="px-1 pb-1">
          <p class="text-muted mb-1">
            {{ $t('Campos deixados em "Herdar" continuam seguindo a Configuração base.') }}
          </p>

          <b-row>
            <b-col md="3">
              <!-- Tri-estado (Herdar/Habilitado/Desabilitado): usamos b-form-select nativo porque o
                   vue-select não seleciona/exibe corretamente valores "falsy" (null e false). -->
              <label
                :for="`day-${day.dayOfWeek}-enabled`"
                class="d-block"
              >{{ $t('Delivery neste dia') }}</label>
              <b-form-select
                :id="`day-${day.dayOfWeek}-enabled`"
                v-model="day.deliveryEnabled"
                :options="enabledOptions"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                :id="`day-${day.dayOfWeek}-start`"
                v-model="day.deliveryStartTime"
                type="text-mask"
                :mask="['##:##']"
                :name="`day_${day.dayOfWeek}_start`"
                :label="$t('Abre')"
                :placeholder="$t('Herdar (horário da loja)')"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                :id="`day-${day.dayOfWeek}-end`"
                v-model="day.deliveryEndTime"
                type="text-mask"
                :mask="['##:##']"
                :name="`day_${day.dayOfWeek}_end`"
                :label="$t('Fecha')"
                :placeholder="$t('Herdar (horário da loja)')"
              />
            </b-col>
          </b-row>

          <b-row v-if="hasIncompleteWindow(day)">
            <b-col>
              <small class="text-danger">
                {{ $t('Preencha "Abre" e "Fecha" juntos, ou deixe ambos vazios para herdar o horário da loja.') }}
              </small>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="3">
              <FormulateInput
                :id="`day-${day.dayOfWeek}-min-order`"
                v-model="day.minOrderValue"
                type="text-number"
                filter="currency"
                currency="R$"
                :precision="2"
                :name="`day_${day.dayOfWeek}_min_order`"
                :label="$t('Valor mínimo (R$)')"
                :placeholder="inheritMinOrderPlaceholder"
              />
            </b-col>
            <b-col md="3">
              <FormulateInput
                :id="`day-${day.dayOfWeek}-fee-per-km`"
                v-model="day.deliveryFeePerKilometer"
                type="text-number"
                filter="currency"
                currency="R$"
                :precision="2"
                :name="`day_${day.dayOfWeek}_fee_per_km`"
                :label="$t('Taxa por KM')"
                :placeholder="inheritPerKmPlaceholder"
              />
            </b-col>
          </b-row>

          <hr>

          <b-alert
            v-if="isInheriting(day.dayOfWeek)"
            variant="secondary"
            show
          >
            <div class="alert-body">
              {{ $t('As faixas de taxa deste dia seguem as faixas padrão da base:') }}
              <ul class="mb-0 mt-50">
                <li
                  v-for="(tier, tIndex) in config.defaultTiers"
                  :key="tIndex"
                >
                  {{ $t('A partir de') }} {{ formatCurrency(tier.productsValueFrom) }} —
                  {{ tier.value === 0 ? $t('Frete grátis') : formatCurrency(tier.value) }}
                </li>
              </ul>
            </div>
          </b-alert>
          <b-alert
            v-else
            variant="warning"
            show
          >
            <div class="alert-body">
              {{ $t('Este dia usa faixas próprias (não herda mais o padrão). A faixa a partir de R$ 0,00 é obrigatória.') }}
            </div>
          </b-alert>

          <delivery-fee-tiers
            :tiers="day.tiers"
            :name="`day_${day.dayOfWeek}_tiers`"
            :label="$t('Faixas de taxa deste dia')"
          />
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import { BRow, BCol, BCard, BCollapse, BAlert, BBadge, BFormCheckbox, BFormSelect } from 'bootstrap-vue'
import { storeDomains } from '@/mixins'
import DeliveryFeeTiers from './DeliveryFeeTiers.vue'

/**
 * Grade dos sete dias (dom → sáb) da tela de Configuração de Delivery (D28), reformulada para um
 * acordeão simples: cada dia começa "Seguindo o padrão" e só expõe os campos de override quando o
 * usuário liga o botão "Personalizar".
 *
 * Enquanto um dia não é personalizado, todos os seus campos ficam nulos e ele herda o padrão da
 * config (D6) e as faixas padrão (D9). Ligar "Personalizar" apenas revela os campos (que começam
 * em "Herdar"); desligar zera os overrides e as faixas, devolvendo o dia à herança. Cada campo
 * segue nulo = herda o padrão (herança por campo preservada), evitando congelar valores.
 * Não há tipo de cobrança por dia: faixas (taxa única) e taxa por KM convivem, e o pedido escolhe.
 */
export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCollapse,
    BAlert,
    BBadge,
    BFormCheckbox,
    BFormSelect,
    DeliveryFeeTiers,
  },

  mixins: [storeDomains],

  props: {
    // Config em edição (mesma referência do estado Vuex; mutada via v-model).
    config: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      // Dias abertos para personalização (revelam os campos de override).
      customizingDays: [],
    }
  },

  computed: {
    // Tri-estado da habilitação do dia: herdar (null) / habilitado (true) / desabilitado (false).
    // b-form-select usa `text` como rótulo da opção.
    enabledOptions() {
      return [
        { value: null, text: `${this.$t('Herdar padrão')} (${this.defaultEnabledLabel})` },
        { value: true, text: this.$t('Habilitado') },
        { value: false, text: this.$t('Desabilitado') },
      ]
    },

    dayLabelMap() {
      return this.dayOfWeekOptions().reduce((acc, option) => {
        acc[option.value] = option.label
        return acc
      }, {})
    },

    // --- Resumo do padrão (configuração base) herdado pelos dias sem personalização ---

    defaultEnabledLabel() {
      return this.config.defaultDeliveryEnabled ? this.$t('Habilitado') : this.$t('Desabilitado')
    },

    // A taxa por KM é opcional: sem ela o pedido não pode ser cobrado por distância.
    defaultPerKmLabel() {
      const value = this.config.defaultDeliveryFeePerKilometer
      if (value === null || value === undefined) return this.$t('Não cadastrada')
      return this.formatCurrency(value)
    },

    defaultMinOrderLabel() {
      const value = this.config.defaultMinOrderValue
      if (value === null || value === undefined || Number(value) === 0) {
        return this.$t('Sem mínimo')
      }
      return this.formatCurrency(value)
    },

    // Placeholders que mostram, em cada campo, o valor que seria herdado do padrão da base.
    inheritMinOrderPlaceholder() {
      return `${this.$t('Herdar')}: ${this.defaultMinOrderLabel}`
    },

    inheritPerKmPlaceholder() {
      return `${this.$t('Herdar')}: ${this.defaultPerKmLabel}`
    },
  },

  watch: {
    // Ao trocar a config em edição (base ↔ campanha, ou recarregar), reabre os dias que já têm
    // personalização salva para que apareçam expandidos.
    config: {
      immediate: true,
      handler() {
        this.syncCustomizingDays()
      },
    },
  },

  methods: {
    dayLabel(dayOfWeek) {
      return this.dayLabelMap[dayOfWeek] || dayOfWeek
    },

    // Sincroniza a lista de dias abertos com os dias que já possuem algum override salvo.
    syncCustomizingDays() {
      this.customizingDays = (this.config.days || [])
        .filter(day => this.isCustomized(day))
        .map(day => day.dayOfWeek)
    },

    // Verdadeiro quando o dia tem QUALQUER override próprio (campo preenchido ou faixa própria).
    isCustomized(day) {
      return (
        day.deliveryEnabled !== null
        || !!day.deliveryStartTime
        || !!day.deliveryEndTime
        || day.deliveryFeePerKilometer !== null
        || day.minOrderValue !== null
        || (day.tiers && day.tiers.length > 0)
      )
    },

    isShown(dayOfWeek) {
      return this.customizingDays.includes(dayOfWeek)
    },

    // Liga/desliga a personalização do dia. Ligar só revela os campos (que começam em "Herdar");
    // desligar zera todos os overrides e faixas, devolvendo o dia à herança do padrão.
    togglePersonalize(day, checked) {
      if (checked) {
        if (!this.customizingDays.includes(day.dayOfWeek)) {
          this.customizingDays = [...this.customizingDays, day.dayOfWeek]
        }
        return
      }
      this.resetDay(day)
      this.customizingDays = this.customizingDays.filter(d => d !== day.dayOfWeek)
    },

    // Zera os overrides do dia (volta a herdar o padrão em todos os campos e faixas).
    resetDay(day) {
      this.$set(day, 'deliveryEnabled', null)
      this.$set(day, 'deliveryStartTime', null)
      this.$set(day, 'deliveryEndTime', null)
      this.$set(day, 'deliveryFeePerKilometer', null)
      this.$set(day, 'minOrderValue', null)
      if (day.tiers && day.tiers.length) {
        day.tiers.splice(0, day.tiers.length)
      }
    },

    // Verdadeiro quando o dia não tem faixas próprias e herda as padrão (D9).
    isInheriting(dayOfWeek) {
      const day = this.config.days.find(d => d.dayOfWeek === dayOfWeek)
      return !day || !day.tiers || day.tiers.length === 0
    },

    // Janela incompleta: só um entre "Abre"/"Fecha" preenchido (I7). Ambos vazios = herda a loja.
    hasIncompleteWindow(day) {
      return !!day.deliveryStartTime !== !!day.deliveryEndTime
    },

    formatCurrency(value) {
      return this.$options.filters?.currency
        ? this.$options.filters.currency(value)
        : `R$ ${Number(value || 0).toFixed(2)}`
    },
  },
}
</script>

<style></style>
